// MacroRing — Whoop-inspired circular progress indicator with a focal
// metric centered inside. Used on the Dashboard for "calories today" and
// shrunk on the Today tab as a glanceable strip.
//
// The ring fills clockwise from 12 o'clock. When consumption exceeds the
// target, the visible arc clamps at 100% (a separate `over` text states
// the surplus) so a 200% day doesn't visually wrap and look like 0%.
//
// A single quiet meter: a grey well for the track, one green arc for the
// fill, amber once you go past target. We DON'T pull in a chart library —
// this is a single SVG circle and an animated stroke-dashoffset.

import { useEffect, useState } from 'react';

export function MacroRing({
  consumed,
  target,
  unit = 'kcal',
  size = 180,
  stroke = 14,
  centerLabel,
}: {
  consumed: number;
  target: number;
  unit?: string;
  size?: number;
  stroke?: number;
  centerLabel?: string;
}) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = target > 0 ? Math.min(consumed / target, 1) : 0;
  const dashOffset = circumference * (1 - ratio);
  const remaining = Math.max(0, target - consumed);
  const over = consumed - target;
  const isOver = over > 0;

  // Draw the ring from empty on mount, then let value changes transition
  // normally. Reduced-motion users snap via the global transition guard.
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setFilled(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={target}
      aria-valuenow={consumed}
      aria-label={`${consumed} of ${target} ${unit}`}
    >
      <svg width={size} height={size} className="rotate-[-90deg]">
        {/* Track — the sunken well, not a hairline: a 9%-alpha line is
            invisible at a 14px stroke on white. */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          className="text-bg-sunken"
          strokeWidth={stroke}
        />
        {/* Progress */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          className={isOver ? 'text-warn-mid' : 'text-good-mid'}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={filled ? dashOffset : circumference}
          style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(0.65, 0, 0.35, 1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span
          className={`font-display leading-none tabular-nums ${
            isOver ? 'text-warn' : 'text-fg'
          }`}
          style={{ fontSize: size * 0.28 }}
        >
          {isOver ? `+${over}` : consumed}
        </span>
        <span
          className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint mt-1"
        >
          {centerLabel ?? (isOver ? `over · ${target} ${unit}` : `of ${target} ${unit}`)}
        </span>
        {!isOver && remaining > 0 && (
          <span className="font-mono text-[10px] tracking-[0.12em] text-fg-muted mt-0.5 tabular-nums">
            {remaining} left
          </span>
        )}
      </div>
    </div>
  );
}
